(() => {
  if (window.__dltEditorialPdpReady) return;
  window.__dltEditorialPdpReady = true;

  const activateTab = (root, nextTab) => {
    const tabs = [...root.querySelectorAll('[data-dlt-editorial-tab]')];
    const panels = [...root.querySelectorAll('[data-dlt-editorial-panel]')];
    if (!tabs.length || !nextTab) return;

    tabs.forEach((tab) => {
      const selected = tab === nextTab;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.id !== nextTab.getAttribute('aria-controls');
      panel.tabIndex = panel.hidden ? -1 : 0;
    });
  };

  const bindTabs = (scope = document) => {
    scope.querySelectorAll('[data-dlt-editorial-tabs], [data-dlt-science-tabs]').forEach((root) => {
      if (root.dataset.dltTabsReady === 'true') return;
      root.dataset.dltTabsReady = 'true';

      const tabs = [...root.querySelectorAll('[data-dlt-editorial-tab]')];
      if (!tabs.length) return;

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activateTab(root, tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          activateTab(root, tabs[nextIndex]);
          tabs[nextIndex].focus();
        });
      });
      // A missing first metaobject must not leave every rendered panel hidden.
      activateTab(root, tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
  };

  const guides = new Map();
  const bindGuide = (nav) => {
    if (guides.has(nav)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const slot = nav.closest('[data-dlt-guide-slot]');
    let frame = 0;
    let targets = [];
    let active = null;
    let lastOffset = -1;
    const refresh = () => {
      targets = links.map((link) => ({ link, section: document.getElementById(link.hash.slice(1)) }))
        .filter(({ link, section }) => {
          link.hidden = !section || !section.getClientRects().length;
          return !link.hidden;
        }).sort((a, b) => a.section.compareDocumentPosition(b.section) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
      schedule();
    };
    const update = () => {
      frame = 0;
      const header = document.querySelector('store-header');
      const announcement = document.querySelector('.shopify-section--announcement-bar');
      const headerSection = header?.closest('.shopify-section--header');
      const headerTop = headerSection?.getBoundingClientRect().top || 0;
      const headerBottom = header && header.dataset.dltScrollState !== 'hidden'
        ? Math.max(0, headerTop + header.offsetHeight) : 0;
      const announcementBottom = Math.max(0, announcement?.getBoundingClientRect().bottom || 0);
      const offset = Math.round(Math.max(headerBottom, announcementBottom));
      if (offset !== lastOffset) {
        nav.style.setProperty('--dlt-guide-top', `${offset}px`);
        lastOffset = offset;
      }
      const height = nav.offsetHeight;
      const hero = document.querySelector('.shopify-section--main-product [data-dlt-main-product]') || document.querySelector('.shopify-section--main-product');
      const blockers = [...document.querySelectorAll('footer:not(.dlt-product-technical-footer), .shopify-section-group-footer-group, .shopify-section--newsletter, [data-dlt-sticky-suppress]')]
        .filter((node) => !node.closest('.dlt-pdp-sticky-cta, .jdgm-widget, .dlt-product-reviews-app, .shopify-section--main-product'));
      const blocked = blockers.some((node) => {
        const rect = node.getBoundingClientRect();
        return rect.height > 0 && rect.top <= offset + height && rect.bottom > 0;
      });
      const overlay = document.querySelector('cart-drawer[open], .drawer[open], dialog[open], [aria-modal="true"][open]');
      const visible = targets.length > 0 && hero && hero.getBoundingClientRect().bottom <= offset && !blocked && !overlay;
      nav.classList.toggle('is-floating', Boolean(visible));
      const hiddenValue = visible ? 'false' : 'true';
      if (nav.getAttribute('aria-hidden') !== hiddenValue) nav.setAttribute('aria-hidden', hiddenValue);
      nav.inert = !visible;
      if (slot) slot.style.minHeight = `${height}px`;
      const readingLine = offset + height + 48;
      let next = targets[0];
      targets.forEach((target) => {
        if (target.section.getBoundingClientRect().top <= readingLine) next = target;
      });
      if (next?.link !== active) {
        links.forEach((link) => link.removeAttribute('aria-current'));
        active = next?.link;
        if (active) {
          active.setAttribute('aria-current', 'location');
          // Keep the selected guide item visible without scrolling the page.
          const left = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
          nav.scrollTo({ left: Math.max(0, left), behavior: 'instant' });
        }
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    nav.classList.add('is-enhanced');
    nav.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      const target = targets.find((item) => item.link === link);
      if (!target || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
      event.preventDefault();
      const parentDetails = target.section.closest('details');
      if (parentDetails) parentDetails.open = true;
      // Reserve the header only when this anchor will reveal it on upward scroll.
      const header = document.querySelector('store-header');
      const targetTop = target.section.getBoundingClientRect().top + window.scrollY;
      const currentOffset = Math.max(0, lastOffset);
      const scrollingUp = targetTop - currentOffset - nav.offsetHeight - 16 < window.scrollY - 2;
      const autoHideHeader = header?.hasAttribute('hide-on-scroll') && header?.hasAttribute('sticky');
      const announcementBottom = document.querySelector('.shopify-section--announcement-bar')?.getBoundingClientRect().bottom || 0;
      const anchorOffset = autoHideHeader
        ? (scrollingUp ? Math.max(currentOffset, header.offsetHeight) : Math.max(0, announcementBottom))
        : currentOffset;
      const y = targetTop - anchorOffset - nav.offsetHeight - 16;
      window.scrollTo({ top: Math.max(0, y), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      history.replaceState(null, '', link.hash);
      target.section.setAttribute('tabindex', '-1');
      target.section.focus({ preventScroll: true });
      schedule();
    }, { signal });
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', refresh, { passive: true, signal });
    window.addEventListener('pageshow', refresh, { signal });
    document.addEventListener('dlt:header:visibility', schedule, { signal });
    document.addEventListener('toggle', schedule, { capture: true, signal });
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList')) refresh(); else schedule();
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'aria-hidden'] });
    guides.set(nav, () => { controller.abort(); observer.disconnect(); cancelAnimationFrame(frame); guides.delete(nav); });
    refresh();
  };
  const scan = (scope = document) => {
    bindTabs(scope);
    scope.querySelectorAll('[data-dlt-pdp-guide]').forEach(bindGuide);
  };
  scan();
  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target);
    // A review/science section may arrive after the guide in the Theme Editor.
    window.dispatchEvent(new Event('resize'));
  });
  document.addEventListener('shopify:section:unload', (event) => {
    guides.forEach((destroy, nav) => { if (event.target.contains(nav)) destroy(); });
  });
})();
